import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import QRCode from "qrcode";
import type { AgentRow, AuditRow } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { jobTitle } from "../lib/labels.ts";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Banner, Button, Card, Dot, Empty, ErrorState, Field, Tile, type Tone } from "../components/Ui.tsx";

/**
 * Güvenlik: TOTP kurulumu, agent'lar ve denetim kaydı.
 *
 * TOTP burada süs değil — geri alınamaz işler (Play yüklemesi, mağaza görseli
 * yükleme, toplu bildirim, kalıcı silme) onsuz ÇALIŞMAZ (sunucu 403 döner).
 * Restart gibi saniyeler içinde geri gelen işler bilerek tek onayla çalışır.
 */
export function SecurityPage(): ReactNode {
  const [secret, setSecret] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const me = useQuery({ queryKey: ["me"], queryFn: () => client.me() });
  const agents = useQuery({ queryKey: ["agents"], queryFn: () => client.agents() });
  const audit = useQuery({ queryKey: ["audit"], queryFn: () => client.audit() });

  useEffect(() => {
    if (!secret || !me.data) return;
    const uri = `otpauth://totp/${encodeURIComponent("GWR Control")}:${encodeURIComponent(
      me.data.email,
    )}?secret=${secret}&issuer=${encodeURIComponent("GWR Control")}&algorithm=SHA1&digits=6&period=30`;
    QRCode.toDataURL(uri, {
      margin: 1,
      width: 200,
      color: { dark: "#e9e9ed", light: "#232532" },
    })
      .then(setQr)
      .catch(() => setError("QR kodu çizilemedi — kurulum anahtarını elle gir."));
  }, [secret, me.data]);

  async function startSetup(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setSecret((await client.totpSetup()).secret);
    } catch (err) {
      setError(humanizeError(err, "Kurulum başlatılamadı"));
    } finally {
      setBusy(false);
    }
  }

  async function enable(value = code): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await client.totpEnable(value);
      setSecret(null);
      setQr(null);
      setCode("");
      await me.refetch();
    } catch (err) {
      setError(humanizeError(err, "Kod doğrulanamadı"));
    } finally {
      setBusy(false);
    }
  }

  const enabled = me.data?.totpEnabled ?? false;
  const agentTile = agentSummary(agents.data, agents.isLoading, agents.error);

  return (
    <>
      <PageHeader kicker="sistem" title="Güvenlik" />

      <PageBody>
        {me.error && (
          <Card flush>
            <ErrorState error={me.error} onRetry={() => void me.refetch()} />
          </Card>
        )}

        {/* me okunamadıysa "TOTP kapalı" demek yanlış olur — bilinmiyor. */}
        {me.data && !enabled && (
          <Banner kicker="kilit">
            TOTP kapalı — Play yüklemesi, toplu bildirim ve kalıcı silme gibi geri alınamaz
            işler <strong>çalışmaz</strong>.
          </Banner>
        )}

        <div className="grid gap-3 lg:grid-cols-3">
          <Tile
            label="TOTP"
            value={me.error ? "?" : me.isLoading ? "…" : enabled ? "etkin" : "kapalı"}
            note={
              me.error
                ? "okunamadı"
                : me.isLoading
                  ? "yükleniyor"
                  : enabled
                    ? "geri alınamaz işler açık"
                    : "geri alınamaz işler kilitli"
            }
            tone={me.error ? "danger" : me.isLoading ? "idle" : enabled ? "ok" : "danger"}
          />
          <Tile
            label="Oturum"
            value={me.error ? "?" : me.isLoading ? "…" : me.data?.totpFresh ? "doğrulanmış" : "normal"}
            note={me.error ? "okunamadı" : (me.data?.email ?? "")}
            tone={me.error ? "danger" : me.isLoading ? "idle" : me.data?.totpFresh ? "ok" : "accent"}
          />
          <Tile
            label="Agent token"
            value={agentTile.value}
            note={agentTile.note}
            tone={agentTile.tone}
          />
        </div>

        {me.data && !enabled && (
          <Card title="İki adımlı doğrulamayı kur">
            {!secret ? (
              <Button variant="primary" onClick={() => void startSetup()} loading={busy}>
                Kurulumu başlat
              </Button>
            ) : (
              <div className="grid max-w-[420px] gap-3.5">
                <p className="text-sm text-neutral-300">
                  Authenticator uygulamanla (Google Authenticator, Authy, 1Password…) kodu okut:
                </p>
                {qr && (
                  <img
                    src={qr}
                    alt="TOTP QR kodu"
                    className="mx-auto rounded-[8px] border border-neutral-800"
                    width={200}
                    height={200}
                  />
                )}
                <details className="text-xs text-neutral-500">
                  <summary className="cursor-pointer">QR okutamıyorsan: kurulum anahtarı</summary>
                  <code className="mono mt-2 block rounded-[6px] bg-black/25 p-3 break-all text-neutral-300">
                    {secret}
                  </code>
                </details>
                <Field label="Uygulamadaki 6 haneli kod">
                  <input
                    className="input mono text-center text-lg tracking-[0.3em]"
                    value={code}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setCode(v);
                      // 6. hanede kendiliğinden doğrula; ayrıca düğmeye basmaya gerek yok.
                      if (v.length === 6 && !busy) void enable(v);
                    }}
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    disabled={busy}
                    autoFocus
                  />
                </Field>
                <Button
                  variant="primary"
                  onClick={() => void enable()}
                  loading={busy}
                  disabled={code.length !== 6}
                >
                  Doğrula ve etkinleştir
                </Button>
                <p className="text-2xs text-neutral-600">
                  ⚠ Kurtarma kodu yok. Telefonunu kaybedersen sunucuda{" "}
                  <code className="mono">admin_user.totp_secret</code> temizlenerek sıfırlanır.
                </p>
              </div>
            )}
            {error && (
              <p role="alert" className="mt-3 text-xs text-danger">
                {error}
              </p>
            )}
          </Card>
        )}

        <PasswordCard totpEnabled={enabled} />

        <Card title="Agent'lar" flush>
          {agents.error ? (
            <ErrorState error={agents.error} onRetry={() => void agents.refetch()} />
          ) : agents.isLoading ? (
            <Empty>Yükleniyor…</Empty>
          ) : agents.data?.length ? (
            agents.data.map((agent) => (
              <div key={agent.id} className="row flex-wrap gap-x-3 gap-y-1">
                <Dot tone={agent.online ? "ok" : "idle"} glow={agent.online} />
                <span className="text-neutral-100">{agent.name}</span>
                <span className="min-w-0 truncate text-xs text-neutral-500 lg:min-w-[160px]">
                  {agent.os ?? "—"}
                </span>
                <span className="text-xs text-neutral-600">{agent.capabilities.length} yetenek</span>
                <span className="mono ml-auto text-2xs text-neutral-600">
                  {agent.online
                    ? "bağlı"
                    : agent.lastSeen
                      ? `son görülme ${new Date(agent.lastSeen).toLocaleString("tr-TR")}`
                      : "hiç bağlanmadı"}
                </span>
              </div>
            ))
          ) : (
            <Empty>Kayıtlı agent yok</Empty>
          )}
        </Card>

        <Card title="Denetim kaydı" hint="kim · ne zaman · ne yaptı" flush>
          {audit.error ? (
            <ErrorState error={audit.error} onRetry={() => void audit.refetch()} />
          ) : audit.isLoading ? (
            <Empty>Yükleniyor…</Empty>
          ) : audit.data?.length ? (
            audit.data.slice(0, 40).map((entry) => {
              const failed = entry.result === "failed";
              return (
                <div key={entry.id} className="row flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2">
                  <span className="mono w-[86px] shrink-0 text-2xs text-neutral-600">
                    {new Date(entry.ts).toLocaleString("tr-TR", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span className="grid min-w-0 flex-1 basis-[180px] gap-0.5">
                    <span className={`text-sm ${failed ? "text-danger" : "text-neutral-200"}`}>
                      {auditLabel(entry)}
                    </span>
                    <span className="mono truncate text-2xs text-neutral-600">
                      {entry.action}
                      {entry.target ? ` · ${entry.target}` : ""}
                    </span>
                  </span>
                  <span className={`ml-auto text-2xs ${failed ? "text-danger" : "text-neutral-600"}`}>
                    {entry.result ?? ""}
                  </span>
                </div>
              );
            })
          ) : (
            <Empty>Kayıt yok</Empty>
          )}
        </Card>
      </PageBody>
    </>
  );
}

/** Agent sorgusu hata verdiyse 0/0 "hepsi açık" değildir — bilinmiyor. */
function agentSummary(
  agents: AgentRow[] | undefined,
  loading: boolean,
  error: unknown,
): { value: string; note: string; tone: Tone } {
  if (error) return { value: "?", note: "agent listesi okunamadı", tone: "danger" };
  if (loading || !agents) return { value: "…", note: "yükleniyor", tone: "idle" };
  if (agents.length === 0) return { value: "0", note: "kayıtlı agent yok", tone: "warn" };
  const online = agents.filter((a) => a.online).length;
  return {
    value: `${online}/${agents.length}`,
    note: "pepper sunucuda, sır agent'ta",
    tone: online === agents.length ? "ok" : online === 0 ? "danger" : "warn",
  };
}

const AUDIT_LABELS: Record<string, string> = {
  "auth.login": "Giriş",
  "auth.logout": "Çıkış",
  "auth.password": "Parola değişti",
  "auth.totp": "Girişte TOTP kodu",
  "auth.totp.enable": "TOTP etkinleştirildi",
  "auth.confirm": "İşlem onayı (TOTP)",
  "agent.connect": "Agent bağlandı",
  "job.cancel": "İş iptal edildi",
  "job.finish": "İş bitti",
  "flag.set": "Özellik bayrağı değişti",
  "flag.delete": "Özellik bayrağı silindi",
  "push.test": "Test bildirimi",
  "push.broadcast": "Toplu bildirim",
  "moderation.delete": "Yorum silindi",
  "moderation.hide": "Yorum gizlendi",
  "moderation.unhide": "Yorum gösterildi",
  "moderation.warn": "Kullanıcı uyarıldı",
  "moderation.suspend": "Kullanıcı askıya alındı",
  "moderation.unsuspend": "Askı kaldırıldı",
  "streamFailure.resolve": "Yayın hatası çözüldü",
  "streamFailure.reopen": "Yayın hatası yeniden açıldı",
};

/**
 * Denetim satırının okunur adı. job.create'in hedefi "<tip>[:<uygulama>]" — iş
 * adına çevrilebiliyor; bilinmeyen eylemler ham haliyle kalır (ikincil satırda zaten var).
 */
function auditLabel(entry: AuditRow): string {
  if (entry.action === "job.create" && entry.target) {
    const [type = "", app] = entry.target.split(":");
    return `${jobTitle(type)}${app ? ` · ${app}` : ""}`;
  }
  const base = AUDIT_LABELS[entry.action] ?? entry.action;
  return entry.result === "failed" ? `${base} — başarısız` : base;
}

/**
 * Parola değiştirme. Parolayı unutmuş ama oturumu açık kullanıcı için mevcut parola
 * yerine TOTP kodu da kabul edilir (sunucu kodu bu istekte doğrular).
 */
function PasswordCard({ totpEnabled }: { totpEnabled: boolean }): ReactNode {
  const [proof, setProof] = useState<"password" | "totp">(totpEnabled ? "totp" : "password");
  const [current, setCurrent] = useState("");
  const [totp, setTotp] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (totpEnabled) setProof("totp");
  }, [totpEnabled]);

  const proofReady = proof === "password" ? current.length > 0 : totp.length === 6;
  const mismatch = repeat.length > 0 && next !== repeat;
  const canSubmit = !busy && proofReady && next.length >= 8 && next === repeat;

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await client.changePassword(
        proof === "password"
          ? { currentPassword: current, newPassword: next }
          : { totp, newPassword: next },
      );
      setCurrent("");
      setTotp("");
      setNext("");
      setRepeat("");
      setDone(true);
    } catch (err) {
      setError(humanizeError(err, "Parola değiştirilemedi"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Parola" hint="değişince diğer cihazlardaki oturumlar kapanır">
      {/* form: Enter gönderir. */}
      <form
        className="grid max-w-[420px] gap-3.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) void submit();
        }}
      >
        {totpEnabled && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant={proof === "totp" ? "primary" : "secondary"}
              aria-pressed={proof === "totp"}
              onClick={() => setProof("totp")}
            >
              TOTP koduyla
            </Button>
            <Button
              variant={proof === "password" ? "primary" : "secondary"}
              aria-pressed={proof === "password"}
              onClick={() => setProof("password")}
            >
              Mevcut parolayla
            </Button>
          </div>
        )}

        {proof === "password" ? (
          <Field label="Mevcut parola">
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
        ) : (
          <Field label="Authenticator'daki 6 haneli kod">
            <input
              className="input mono text-center text-lg tracking-[0.3em]"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </Field>
        )}

        <Field label="Yeni parola · en az 8 karakter">
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        <Field label="Yeni parola (tekrar)" error={mismatch ? "Parolalar aynı değil" : undefined}>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
        </Field>

        <Button variant="primary" type="submit" loading={busy} disabled={!canSubmit}>
          Parolayı değiştir
        </Button>
        {!totpEnabled && (
          <p className="text-2xs text-neutral-600">
            TOTP kapalıyken yalnızca mevcut parolayla değiştirilebilir.
          </p>
        )}
        {error && (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
        {done && (
          <p role="status" className="text-xs text-ok">
            Parola değişti.
          </p>
        )}
      </form>
    </Card>
  );
}
